import { useEffect, useState } from "react";
import axios from "axios";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUMMER_SERVICES = [
  {
    name: "Lawn Mowing",
    description:
      "Our lawn mowing service is designed to keep your grass looking neat, healthy, and well-maintained. We use professional-grade equipment to ensure an even cut every time, promoting strong growth and a polished appearance. Whether scheduled weekly or bi-weekly, we tailor our service to meet the specific needs of your lawn.",
  },
  {
    name: "Trimming & Edging",
    description:
      "Enhance the look of your property with precise trimming and edging. We carefully define borders along driveways, walkways, and garden beds while trimming around obstacles such as trees and fences. This attention to detail creates sharp, clean lines that elevate your lawn's overall appearance.",
  },
  {
    name: "Seasonal Cleanups",
    description:
      "Our seasonal cleanup services prepare your property for the changing seasons. In the spring, we clear away winter debris to promote healthy growth, while fall cleanups focus on leaf removal and yard preparation before colder weather sets in. We ensure your outdoor space remains clean, safe, and ready for the season ahead.",
  },
  {
    name: "Property Maintenance",
    description:
      "Keep your property in top condition year-round with our comprehensive maintenance services. We offer ongoing care tailored to your needs, ensuring your outdoor spaces remain tidy, functional, and visually appealing. From routine upkeep to detailed attention, we provide dependable service you can count on.",
  },
];

const WINTER_SERVICES = [
  {
    name: "Snow Plowing",
    description:
      "Our snow plowing service ensures your driveways and parking areas remain clear, safe, and accessible during the winter months. Using reliable equipment and timely service, we efficiently remove snow buildup to minimize disruptions and keep your property fully functional after every snowfall.",
  },
  {
    name: "Deicing",
    description:
      "Prevent hazardous conditions with our professional deicing services. We apply effective materials to reduce ice buildup on driveways, walkways, and high-traffic areas, helping to improve safety and reduce the risk of slips and falls during freezing conditions.",
  },
  {
    name: "Salt Spreading",
    description:
      "Our salt spreading service provides an added layer of protection against ice formation. We strategically apply salt to key areas of your property to maintain safe surfaces and ensure continued accessibility throughout harsh winter weather.",
  },
];

function App() {
  const [theme, setTheme] = useState("summer");
  const [effects, setEffects] = useState([]);
  const [activeService, setActiveService] = useState(null);
  const [effectsEnabled, setEffectsEnabled] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "idle", message: "" });

  useEffect(() => {
    if (!effectsEnabled) {
      setEffects([]);
      return;
    }
    const count = theme === "winter" ? 25 : 20;
    const items = Array.from({ length: count }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      duration: (theme === "winter" ? 3 : 4) + Math.random() * (theme === "winter" ? 5 : 6),
      delay: Math.random() * 5,
      symbol: theme === "winter" ? "❄" : "🍃",
      cls: theme === "winter" ? "snowflake" : "leaf",
    }));
    setEffects(items);
  }, [theme, effectsEnabled]);

  useEffect(() => {
    if (!activeService) return;
    const onKey = (e) => e.key === "Escape" && setActiveService(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [activeService]);

  const services = theme === "summer" ? SUMMER_SERVICES : WINTER_SERVICES;
  const title = theme === "summer" ? "Landscaping Services" : "Snow & Ice Management";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitStatus({ type: "idle", message: "" });
    try {
      await axios.post(`${API}/quote`, {
        name: form.name,
        email: form.email,
        message: form.message,
      });
      setSubmitStatus({
        type: "success",
        message:
          "Thank you for your request. The team at 5J Property Maintenance will review your information and connect with you within 48 hours.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again or call us directly.";
      setSubmitStatus({ type: "error", message: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`App ${theme}`} data-testid="page">
      <nav>
        <div className="nav-brand">
          <img src="/logo.png" alt="5J" className="nav-logo" />
          <strong className="brand-text">
            <span className="brand-5j">5J</span> <span className="brand-pm">Property Maintenance</span>
          </strong>
        </div>
        <div className="toggle">
          <button
            data-testid="summer-toggle-btn"
            onClick={() => setTheme("summer")}
            aria-label="Summer theme"
            className={theme === "summer" ? "active" : ""}
          >
            ☀️
          </button>
          <button
            data-testid="winter-toggle-btn"
            onClick={() => setTheme("winter")}
            aria-label="Winter theme"
            className={theme === "winter" ? "active" : ""}
          >
            ❄️
          </button>
          <button
            data-testid="effects-toggle-btn"
            onClick={() => setEffectsEnabled((v) => !v)}
            aria-label={effectsEnabled ? "Pause falling animation" : "Resume falling animation"}
            title={effectsEnabled ? "Pause falling animation" : "Resume falling animation"}
            className="effects-btn"
          >
            {effectsEnabled ? "⏸" : "▶"}
          </button>
        </div>
      </nav>

      <header id="hero">
        <img src="/logo.png" alt="5J Property Maintenance" className="hero-logo" data-testid="logo" />
        {effects.map((e) => (
          <div
            key={e.id}
            className={e.cls}
            style={{
              left: `${e.left}vw`,
              animationDuration: `${e.duration}s`,
              animationDelay: `${e.delay}s`,
            }}
          >
            {e.symbol}
          </div>
        ))}
      </header>

      <section className="welcome" data-testid="welcome-section">
        <div className="welcome-ornament" aria-hidden="true">
          <span className="ornament-line"></span>
          <span className="ornament-diamond">◆</span>
          <span className="ornament-line"></span>
        </div>
        <h2 className="welcome-heading">Welcome to 5J Property Maintenance</h2>
        <p className="welcome-text">
          Exceptional landscapes don't happen by chance—they're built through precision, consistency, and care. We specialize in delivering professional property maintenance services that elevate the appearance and standard of your outdoor spaces.
        </p>
      </section>

      <section>
        <h2 data-testid="services-title">{title}</h2>
        <p className="services-hint">Click any service to learn more</p>
        <div className="services" data-testid="services-list">
          {services.map((s) => (
            <button
              type="button"
              className="card"
              key={s.name}
              onClick={() => setActiveService(s)}
              data-testid={`service-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <span className="card-title">{s.name}</span>
              <span className="card-more">Learn more →</span>
            </button>
          ))}
        </div>
      </section>

      <section>
        <h2>Our Work</h2>
        <div className="coming-soon" data-testid="our-work-coming-soon">
          <span className="coming-soon-badge">Coming Soon</span>
          <p>We're putting together a gallery of our recent projects. Check back soon!</p>
        </div>
      </section>

      <section>
        <h2>Service Area</h2>
        <iframe
          title="Service Area Map"
          src="https://maps.google.com/maps?q=toronto&t=&z=10&ie=UTF8&iwloc=&output=embed"
          width="100%"
          height="300"
          style={{ border: 0, borderRadius: 10 }}
        />
      </section>

      <section>
        <h2>Request a Quote</h2>
        <form onSubmit={handleSubmit} data-testid="quote-form">
          <input
            type="text"
            placeholder="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            disabled={submitting}
            data-testid="quote-name-input"
          />
          <input
            type="email"
            placeholder="Email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={submitting}
            data-testid="quote-email-input"
          />
          <textarea
            placeholder="Describe your needs"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            disabled={submitting}
            data-testid="quote-message-input"
          />
          <button
            className="submit"
            type="submit"
            disabled={submitting}
            data-testid="quote-submit-btn"
          >
            {submitting ? "Sending…" : "Submit Request"}
          </button>
          {submitStatus.type === "success" && (
            <div className="form-alert form-alert-success" data-testid="quote-success-msg">
              {submitStatus.message}
            </div>
          )}
          {submitStatus.type === "error" && (
            <div className="form-alert form-alert-error" data-testid="quote-error-msg">
              {submitStatus.message}
            </div>
          )}
        </form>
      </section>

      <footer>
        <div className="footer-contact" data-testid="footer-contact">
          <span>Call us:</span>
          <a href="tel:+17057189134" data-testid="footer-phone-link">1-705-718-9134</a>
        </div>
        <div>© 2026 5J Property Maintenance</div>
      </footer>

      {activeService && (
        <div
          className="modal-overlay"
          onClick={() => setActiveService(null)}
          data-testid="service-modal"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              onClick={() => setActiveService(null)}
              aria-label="Close"
              data-testid="service-modal-close-btn"
            >
              ×
            </button>
            <h3 data-testid="service-modal-title">{activeService.name}</h3>
            <p data-testid="service-modal-description">{activeService.description}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
